import { IconButton, Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { BATCH_EDIT_ENUMS, IActivityIndividualItem, IExpiryList } from "../data-handling/finance-inventory-types";
import CtaComponent from "../../../components/reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import deleteIcon from "./../../../assets/images/delete.png"
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { toastError } from "../../../utils/react-toaster";
import { cloneDeep } from "lodash";
import { decimalPriceRegex } from "../../../utils";

type modalProps = {
  open: boolean;
  onClose: () => void;
  batchToEdit: IExpiryList[];
  onSave: (batch: IExpiryList[], quantity: number) => void;
  itemUnderEdit: IActivityIndividualItem | undefined,
};

function BatchQuantityEditModal({ open, onClose, batchToEdit, onSave, itemUnderEdit }: modalProps) {
  const [batches, setBatches] = useState<IExpiryList[]>([]);

  useEffect(() => {
    if (open) {
      setBatches(cloneDeep(batchToEdit))
    }
  }, [batchToEdit, open])

  const onAddPress = () => {
    const itemToAdd: IExpiryList = {
      batchNumber: "",
      itemQty: 0,
    }

    if (itemUnderEdit?.conversion) {
      itemToAdd.conversion = itemUnderEdit.conversion;
    }

    batches.push(itemToAdd);
    setBatches([...batches]);
  };

  const onDelete = (index: number) => {
    if (batches?.length <= 1) {
      return;
    }
    batches.splice(index, 1);
    setBatches([...batches]);
  };

  const onSavePress = () => {
    let quantitySum = 0;
    let quantityError = false;

    const batchesToSubmit = cloneDeep(batches || [])?.map((batch) => {
      const originalQty = batch.itemQty;
      batch.itemQty = Number(batch.itemQty)
      if ((originalQty as any) === "" || batch.itemQty < 0 || isNaN(batch.itemQty)) {
        quantityError = true
      }
      quantitySum += batch.itemQty
      return batch;
    })

    if (quantityError) {
      toastError("Invalid value for quantity")
      return;
    }

    onSave(batchesToSubmit, quantitySum)
    onClose()
  }

  const onValueChange = (type: BATCH_EDIT_ENUMS, value: string, index: number) => {
    const batchToEdit = batches[index]
    if (type === BATCH_EDIT_ENUMS.BATCH_NUMBER) {
      batchToEdit.batchNumber = value
    } else if (type === BATCH_EDIT_ENUMS.QUANTITY) {
      batchToEdit.itemQty = decimalPriceRegex(value)
    }
    setBatches([...batches]);
  }

  return (
    <Modal
      className="flex justify-center items-center"
      open={open}
      onClose={onClose}
    >
      <div className="min-w-[720px] py-22 px-20 rounded-[8px] bg-white relative">
        <IconButton onClick={onClose} className="absolute top-[-40px] bg-[#FFFFFF38] text-[#FFFFFF] right-[-45px]">
          <CloseRoundedIcon/>
        </IconButton>
        <div className="flex flex-row justify-between items-center">
          <div>
            <h1 className="text-lg font-semibold">Add & edit batch details</h1>
            <p className="text-sm mt-2 opacity-60">Enter batch-wise quantity details for this item</p>
          </div>
          <CtaComponent
            customClass="text-[#397EF5] border-0 bg-[#397EF51A]"
            ctaSize={CTA_SIZE.small}
            ctaType={CTA_TYPE.secondary_cta_with_accent}
            onCtaClick={onAddPress}
            ctaText={"+ Add new batch"}
          />
        </div>
        <div className="max-h-[40vh] overflow-auto pt-8">
          {batches?.map((item, idx) => {
            return (
              <BatchEditRow
                onChange={onValueChange}
                onDelete={() => onDelete(idx)}
                key={idx}
                index={idx}
                row={item}
                showBottomBorder={idx < batches?.length - 1}
                hideDeleteButtton={batches?.length <= 1}
              />
            );
          })}
        </div>
        <div className="mt-16 flex justify-end">
          <CtaComponent
            ctaText={"Cancel"}
            ctaType={CTA_TYPE.secondary_active_cta}
            ctaSize={CTA_SIZE.small}
            onCtaClick={onClose}
            customClass={"mr-24"}
          />
          <CtaComponent
            ctaText={"Save changes"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={CTA_SIZE.small}
            onCtaClick={onSavePress}
            customClass={"mr-12"}
          />
        </div>
      </div>
    </Modal>
  );
}

function BatchEditRow({
  row,
  onDelete,
  onChange,
  index,
  hideDeleteButtton = false,
  showBottomBorder = true,
}: {
  index: number,
  row: IExpiryList;
  onDelete: () => void;
  onChange: (type: BATCH_EDIT_ENUMS, value: string, index: number) => void,
  hideDeleteButtton: boolean,
  showBottomBorder: boolean,
}) {
  return (
    <div className={`flex justify-start items-end py-20 ${showBottomBorder ? "border-b border-solid border-[#0000000D]" : ""}`}>
      <div className="flex items-center justify-start mr-30">
        <label className="font-500 text-sm mr-16 text-nowrap">Batch Number</label>
        <input
          onChange={(e) => onChange(BATCH_EDIT_ENUMS.BATCH_NUMBER, e.target.value, index)}
          className="w-[205px] border border-gray-300 text-gray-900 text-sm rounded-[8px] focus:ring-blue-500 focus:border-blue-500 block p-2.5"
          placeholder="Batch Number"
          value={row.batchNumber}
        />
      </div>
      <div className="flex items-center justify-start">
        <label className="font-500 mr-16 text-sm">Quantity<span className="text-[#f54242] font-500">{" *"}</span></label>
        <input
          onChange={(e) => onChange(BATCH_EDIT_ENUMS.QUANTITY, e.target.value, index)}
          className="w-[205px] border border-gray-300 text-gray-900 text-sm rounded-[8px] focus:ring-blue-500 focus:border-blue-500 block p-2.5"
          placeholder="Quantity"
          type="number"
          value={row.itemQty}
        />
      </div>
      {!hideDeleteButtton && <IconButton onClick={onDelete} className="p-8 mb-4 text-rede2574c ml-8">
        <img className="h-24 w-22" src={deleteIcon}/>
      </IconButton>}
    </div>
  );
}

export default BatchQuantityEditModal;
